import type { Editor } from '@tiptap/react';
import type { SlashCommand } from '../types/extension-interface';
import { isNodeInSchema } from './tiptap-utils';
import { isExtensionAvailableInMenu } from './extension-menu-utils';

/**
 * Base slash command configurations for built-in Tiptap extensions
 */
const BASE_SLASH_COMMANDS: SlashCommand[] = [
  {
    title: 'Text',
    subtext: 'Regular text paragraph',
    keywords: ['p', 'paragraph', 'text'],
    badge: 'type',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('paragraph', editor) &&
      isExtensionAvailableInMenu(editor, 'paragraph', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().setParagraph().run();
    },
  },
  {
    title: 'Heading 1',
    subtext: 'Key section heading',
    keywords: ['h1', 'heading1', 'subheading'],
    badge: 'heading-1',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('heading', editor) &&
      isExtensionAvailableInMenu(editor, 'heading', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    },
  },
  {
    title: 'Heading 2',
    subtext: 'Subsection and group heading',
    keywords: ['h2', 'heading2', 'subheading'],
    badge: 'heading-2',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('heading', editor) &&
      isExtensionAvailableInMenu(editor, 'heading', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    },
  },
  {
    title: 'Heading 3',
    subtext: 'Subsection and group heading',
    keywords: ['h3', 'heading4', 'subheading'],
    badge: 'heading-3',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('heading', editor) &&
      isExtensionAvailableInMenu(editor, 'heading', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 4 }).run();
    },
  },
  {
    title: 'Bullet List',
    subtext: 'List with unordered items',
    keywords: ['ul', 'li', 'list', 'bulletlist', 'bullet list'],
    badge: 'list-bullet-alt',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('bulletList', editor) &&
      isExtensionAvailableInMenu(editor, 'bulletList', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleBulletList().run();
    },
  },
  {
    title: 'Numbered List',
    subtext: 'List with ordered items',
    keywords: ['ol', 'li', 'list', 'numberedlist', 'numbered list'],
    badge: 'list-ordered',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('orderedList', editor) &&
      isExtensionAvailableInMenu(editor, 'orderedList', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleOrderedList().run();
    },
  },
  {
    title: 'To-do list',
    subtext: 'List with tasks',
    keywords: ['tasklist', 'task list', 'todo', 'checklist'],
    badge: 'list-todo',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('taskList', editor) &&
      isExtensionAvailableInMenu(editor, 'taskList', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleTaskList().run();
    },
  },
  {
    title: 'Blockquote',
    subtext: 'Blockquote block',
    keywords: ['quote', 'blockquote'],
    badge: 'blockquote',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('blockquote', editor) &&
      isExtensionAvailableInMenu(editor, 'blockquote', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().wrapIn('blockquote').run();
    },
  },
  {
    title: 'Code Block',
    subtext: 'Code block with syntax highlighting',
    keywords: ['code', 'pre'],
    badge: 'code-block',
    group: 'Style',
    check: (editor) =>
      isNodeInSchema('codeBlock', editor) &&
      isExtensionAvailableInMenu(editor, 'codeBlock', 'slash'),
    action: ({ editor }) => {
      editor.chain().focus().toggleNode('codeBlock', 'paragraph').run();
    },
  },
  {
    title: 'Separator',
    subtext: 'Horizontal line to separate content',
    keywords: ['hr', 'horizontalRule', 'line', 'separator'],
    badge: 'minus',
    group: 'Insert',
    check: (editor) => isNodeInSchema('horizontalRule', editor),
    action: ({ editor }) => {
      editor.chain().focus().setHorizontalRule().run();
    },
  },
  {
    title: 'Table',
    subtext: '3x3 table with header row',
    keywords: ['table', 'grid', 'rows', 'columns'],
    badge: 'table',
    group: 'Insert',
    check: (editor) => isNodeInSchema('table', editor),
    action: ({ editor }) => {
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
];

/**
 * Gets all slash command configurations from the editor's extensions
 */
export function getSlashCommandConfigurations(
  editor: Editor | null
): SlashCommand[] {
  if (!editor) {
    return [];
  }

  const configurations: SlashCommand[] = [...BASE_SLASH_COMMANDS];

  // Iterate through all extensions to find those with slash command configuration
  editor.extensionManager.extensions.forEach((extension) => {
    // Check if extension has storage with getSlashConfig
    if (
      extension.storage != null &&
      typeof extension.storage['getSlashConfig'] === 'function'
    ) {
      try {
        const config = extension.storage['getSlashConfig']();
        if (config != null && typeof config === 'object') {
          configurations.push(config);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          `Failed to get slash config for extension ${extension.name}:`,
          error
        );
      }
    }
  });

  return configurations;
}

/**
 * Gets slash command configurations that are available for the current editor state
 */
export function getAvailableSlashCommandConfigurations(
  editor: Editor | null
): SlashCommand[] {
  if (!editor) {
    return [];
  }

  const allConfigs = getSlashCommandConfigurations(editor);

  return allConfigs.filter((config) => {
    try {
      // Use 'available' if provided, otherwise fall back to 'check' for backward compatibility
      const availabilityCheck = config.available ?? config.check;
      return availabilityCheck(editor);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        `Error checking if slash command is available for ${config.title}:`,
        error
      );
      return false;
    }
  });
}
